// Client-side record-shop cart (localStorage). Records are mostly 1-of-1, so
// the cart is a de-duplicated set of stockIds with just enough display info.
// Scoped per host so different venues don't share a bag.
import { useSyncExternalStore } from "react";
import { pushEcommerceEvent } from "./analytics";

export type CartItem = {
  stockId: string;
  artist: string;
  title: string;
  priceCents: number;
  imageUrl: string | null;
};

function key(): string {
  const host = typeof window !== "undefined" ? window.location.host : "server";
  return `assembly:cart:${host}`;
}

function read(): CartItem[] {
  try {
    const raw = typeof window !== "undefined" ? localStorage.getItem(key()) : null;
    return raw ? (JSON.parse(raw) as CartItem[]) : [];
  } catch {
    return [];
  }
}

const listeners = new Set<() => void>();
let snapshot: CartItem[] = read();

function write(items: CartItem[]) {
  snapshot = items;
  try {
    localStorage.setItem(key(), JSON.stringify(items));
  } catch {
    // ignore quota / private-mode failures
  }
  listeners.forEach((l) => l());
}

export function addToCart(item: CartItem) {
  if (snapshot.some((i) => i.stockId === item.stockId)) return; // already in bag
  write([...snapshot, item]);
  // Record shop has no currency field (assumes GBP throughout, see gbp()
  // formatters on the record pages), matching that existing convention.
  pushEcommerceEvent("add_to_cart", {
    currency: "GBP",
    value: item.priceCents / 100,
    items: [
      {
        item_id: item.stockId,
        item_name: `${item.artist}, ${item.title}`,
        item_category: "record",
        price: item.priceCents / 100,
        quantity: 1,
      },
    ],
  });
}
export function removeFromCart(stockId: string) {
  write(snapshot.filter((i) => i.stockId !== stockId));
}
export function clearCart() {
  write([]);
}
export function inCart(stockId: string): boolean {
  return snapshot.some((i) => i.stockId === stockId);
}

export function useCart(): CartItem[] {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => snapshot,
    () => snapshot,
  );
}
